/**
 * AnalyticsPage — /analytics top-level route.
 *
 * Fork Tier-A-#1 (A.2 + A.3 + A.4). Lands Sean on a polished analytics
 * surface composed of three nicelydone-Mixpanel-inspired components
 * re-skinned in Butler brand:
 *   - Row 1: 3× HeroKpiProgressBar (Revenue, Riders/hr, Guests)
 *   - Row 2: 2× CompactScoreTile (Kiosk Health, Plan Mix Diversity)
 *            + LivePulse in a corner
 *
 * Data sources (all graceful-fail):
 *   - /api/dashboard/:eventId/stats    → the 5 hero metrics + hourly series
 *   - /api/dashboard/:eventId/extras   → plan_mix + kiosk_throughput arrays
 *   - /api/dashboard/:eventId/compare  → YoY arrows (may 404 while parent
 *                                        fork's mm_compare_events commit
 *                                        is unpushed — arrows vanish silently)
 *
 * Uses useAuth().currentEventId — same hook the Dashboard uses on /home —
 * so there's no EventSelector shim in this fork. When the user is signed
 * in but no event is selected we render an explanatory empty page instead
 * of firing fetches against eventId=null.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { PageLayout } from '../components/PageLayout'
import { useAuth } from '../contexts/AuthContext'
import { HeroKpiProgressBar } from '../components/analytics/HeroKpiProgressBar'
import { CompactScoreTile } from '../components/analytics/CompactScoreTile'
import { LivePulse, type FeedStatus } from '../components/analytics/LivePulse'
import { RevenueHeatMap } from '../components/analytics/RevenueHeatMap'
import { CapacityGauges } from '../components/analytics/CapacityGauges'

interface HourlyPoint {
  hour: number
  orders: number
  revenue: number
  riders: number
}

interface DashboardStats {
  source: string
  event: { event_id: number; event_name: string; event_date: string; status: string }
  metrics: {
    revenue_total: number
    ride_gross: number
    guests: number
    riders_per_hour: number
    orders_per_hour: number
  }
  hourly: HourlyPoint[]
  generated_at: string
}

interface KioskRow {
  rank: number
  kiosk_group_name: string
  placement_type: string | null
  orders: number
  revenue: number
  new_band_orders: number
  reload_orders: number
}

interface PlanRow {
  rank: number
  plan_name: string
  orders: number
  revenue: number
  pct_of_orders: number
}

interface DashboardExtras {
  source: string
  event_id: number
  plan_mix: PlanRow[]
  kiosk_throughput: KioskRow[]
  generated_at: string
}

interface CompareResponse {
  this_event?: { revenue_total?: number; guests?: number; riders_per_hour?: number }
  prior_event?: { revenue_total?: number; guests?: number; riders_per_hour?: number }
  yoy_pct?: {
    revenue_total?: number
    guests?: number
    riders_per_hour?: number
    orders_per_hour?: number
    ride_gross?: number
  }
}

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n)

/**
 * Derive a sensible capacity benchmark for each hero metric.
 *
 * Per-hour rate metrics: use the best-observed hour × 1.1 (+10% headroom)
 * so the bar reads "how close is right-now to today's peak" — the
 * operator-useful framing.
 *
 * Total metrics (revenue, ride_gross, guests): no natural capacity ceiling
 * exists in the rollup, so we return null and the hero hides the bar
 * rather than invent a fake target.
 */
function deriveCapacity(
  metric: 'riders_per_hr' | 'orders_per_hr' | 'guests' | 'revenue' | 'ride_gross',
  stats: DashboardStats | null,
): number | null {
  if (!stats) return null
  const { hourly, metrics } = stats
  const nonEmpty = hourly && hourly.length > 0
  switch (metric) {
    case 'riders_per_hr': {
      if (!nonEmpty) return null
      const peak = Math.max(...hourly.map((h) => h.riders || 0))
      if (!Number.isFinite(peak) || peak <= 0) return null
      return Math.round(peak * 1.1)
    }
    case 'orders_per_hr': {
      if (!nonEmpty) return null
      const peak = Math.max(...hourly.map((h) => h.orders || 0))
      if (!Number.isFinite(peak) || peak <= 0) return null
      return Math.round(peak * 1.1)
    }
    case 'revenue':
    case 'ride_gross':
    case 'guests':
    default:
      // Intentionally no capacity for totals — returning null suppresses
      // the bar rather than showing an arbitrary "120% of today" target
      // that would mislead.
      void metrics
      return null
  }
}

/**
 * Kiosk Health score — evenness of order distribution across kiosk groups.
 * A perfectly balanced bank scores 100; a deeply unbalanced one (one kiosk
 * doing everything while others idle) drops toward 0. Computed as:
 *
 *   1 - stdev(orders) / mean(orders)   clamped to [0, 1]
 *
 * Returns null when there are fewer than 2 kiosks — not enough variance
 * to score — so the tile shows "Need 2+ kiosks for health score".
 */
function computeKioskHealth(kiosks: KioskRow[] | undefined | null): number | null {
  if (!Array.isArray(kiosks) || kiosks.length < 2) return null
  const orders = kiosks.map((k) => Math.max(0, k.orders || 0))
  const total = orders.reduce((a, b) => a + b, 0)
  if (total <= 0) return null
  const mean = total / orders.length
  if (mean <= 0) return null
  const variance =
    orders.reduce((acc, v) => acc + (v - mean) ** 2, 0) / orders.length
  const stdev = Math.sqrt(variance)
  const cv = stdev / mean
  const score = Math.max(0, 1 - cv) * 100
  return Math.round(score)
}

/**
 * Plan Mix Diversity — inverse-concentration of orders across plan types.
 * Equal split across every plan → 100. All orders on one plan → 0. Based
 * on `1 - HHI` where HHI is the Herfindahl index of plan-share squared.
 */
function computePlanDiversity(plans: PlanRow[] | undefined | null): number | null {
  if (!Array.isArray(plans) || plans.length < 2) return null
  const totalOrders = plans.reduce((a, p) => a + Math.max(0, p.orders || 0), 0)
  if (totalOrders <= 0) return null
  const hhi = plans.reduce((acc, p) => {
    const share = Math.max(0, p.orders || 0) / totalOrders
    return acc + share * share
  }, 0)
  // Normalize: a uniform N-plan distribution has HHI = 1/N. Scale (1 - hhi)
  // against the uniform ceiling so 100% means "as diverse as this plan
  // count allows" rather than "infinitely diverse" (which is unreachable).
  const uniformCeiling = 1 - 1 / plans.length
  if (uniformCeiling <= 0) return 0
  const score = ((1 - hhi) / uniformCeiling) * 100
  return Math.max(0, Math.min(100, Math.round(score)))
}

function captionForScore(pct: number | null): string | undefined {
  if (pct === null) return undefined
  if (pct >= 85) return 'Excellent'
  if (pct >= 70) return 'Above average'
  if (pct >= 55) return 'Healthy'
  if (pct >= 35) return 'Below average'
  return 'Needs attention'
}

export function AnalyticsPage() {
  const { currentEventId } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [statsLoading, setStatsLoading] = useState<boolean>(false)
  const [statsError, setStatsError] = useState<string | null>(null)

  const [extras, setExtras] = useState<DashboardExtras | null>(null)
  const [extrasLoading, setExtrasLoading] = useState<boolean>(false)
  const [extrasAvailable, setExtrasAvailable] = useState<boolean>(true)

  const [yoy, setYoy] = useState<CompareResponse['yoy_pct'] | null>(null)

  // Codex HIGH 2026-04-19: clear all event-scoped state the moment
  // currentEventId changes — otherwise a stale payload from the previous
  // event would remain on screen while the new fetches are in flight (or
  // forever, if one of them fails). The loading skeletons only trigger
  // when the slice is null, so null-ing here is what makes event-switch
  // feel clean instead of leaky.
  useEffect(() => {
    setStats(null)
    setStatsError(null)
    setExtras(null)
    setExtrasAvailable(true)
    setYoy(null)
    if (currentEventId == null) {
      setStatsLoading(false)
      setExtrasLoading(false)
    }
  }, [currentEventId])

  // Codex HIGH 2026-04-19 (pass 2): poll /stats every 30 s so the "Live"
  // claim is backed by real refresh cadence.
  //
  // Codex HIGH 2026-04-19 (pass 5): on a later 401/403/404 the poller
  // must NOT keep stale protected KPIs under a "paused" label. Treat
  // those as access-lost and clear ALL event-scoped state, not just
  // stats (pass-6 refinement: extras + yoy kept rendering otherwise).
  //
  // Codex HIGH 2026-04-19 (pass 6): sequence counter on every request
  // so a slow response can't overwrite a newer one if it arrives late
  // (the live backend path has no statement timeout, so a >30 s poll
  // could race a subsequent fresher poll).
  const pollSeqRef = useRef(0)

  useEffect(() => {
    if (currentEventId == null) return
    let cancelled = false
    let intervalId: number | undefined
    // Reset sequence at the start of each event — prior-event responses
    // arriving late should never win a race against new-event responses.
    pollSeqRef.current = 0

    const fetchOnce = (initial: boolean) => {
      if (cancelled) return
      pollSeqRef.current += 1
      const mySeq = pollSeqRef.current
      const controller = new AbortController()
      if (initial) {
        setStatsLoading(true)
        setStatsError(null)
      }
      fetch(`/api/dashboard/${currentEventId}/stats`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) {
            const err = new Error(`HTTP ${res.status}`) as Error & { httpStatus?: number }
            err.httpStatus = res.status
            throw err
          }
          return res.json() as Promise<DashboardStats>
        })
        .then((data) => {
          if (cancelled || mySeq !== pollSeqRef.current) return
          setStats(data)
          setStatsError(null)
          setStatsLoading(false)
        })
        .catch((err: unknown) => {
          if (cancelled || mySeq !== pollSeqRef.current) return
          if (err && (err as { name?: string }).name === 'AbortError') return
          const httpStatus = (err as { httpStatus?: number })?.httpStatus
          const message = err instanceof Error ? err.message : 'failed'

          // 401/403/404: auth expired / access revoked / event missing.
          // Clear ALL event-scoped state so stale protected KPIs,
          // kiosk-health, and YoY arrows don't linger under a false
          // "paused" label (pass-6 refinement).
          if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404) {
            setStats(null)
            setExtras(null)
            setExtrasAvailable(false)
            setYoy(null)
          }
          // Transient 5xx / network: preserve the last-good snapshot,
          // let LivePulse reflect 'paused' state.
          setStatsError(message)
          if (initial) setStatsLoading(false)
        })
    }

    fetchOnce(true)
    intervalId = window.setInterval(() => fetchOnce(false), 30_000)

    return () => {
      cancelled = true
      if (intervalId !== undefined) window.clearInterval(intervalId)
    }
  }, [currentEventId])

  useEffect(() => {
    if (currentEventId == null) return
    const controller = new AbortController()
    let cancelled = false
    setExtrasLoading(true)
    fetch(`/api/dashboard/${currentEventId}/extras`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<DashboardExtras>
      })
      .then((data) => {
        if (cancelled) return
        setExtras(data)
        setExtrasAvailable(true)
        setExtrasLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        // Fork G known regression: /extras 500s on data-bearing events. The
        // brief says render tile in empty state rather than break the page.
        setExtras(null)
        setExtrasAvailable(false)
        setExtrasLoading(false)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [currentEventId])

  useEffect(() => {
    if (currentEventId == null) return
    const controller = new AbortController()
    let cancelled = false
    // Parent fork's mm_compare_events endpoint is local-only at brief-write
    // time. We still probe — if it's deployed we get real YoY; if not, we
    // silently drop the arrow badges.
    fetch(`/api/dashboard/${currentEventId}/compare`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<CompareResponse>
      })
      .then((data) => {
        if (cancelled) return
        setYoy(data && data.yoy_pct ? data.yoy_pct : null)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        setYoy(null)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [currentEventId])

  const kioskHealth = useMemo(
    () => computeKioskHealth(extras?.kiosk_throughput),
    [extras],
  )
  const planDiversity = useMemo(
    () => computePlanDiversity(extras?.plan_mix),
    [extras],
  )

  // Codex pass-5 HIGH 2026-04-19: the backend can emit any of
  // {'live','upcoming','closed','inactive','active','unknown'} via
  // dashboard-stats.js#inferStatus, plus any string that lands in
  // pg-dashboard-stats.js as the Event.Status column. Collapsing
  // everything other than 'live'/'upcoming' into 'closed' mislabels a
  // degraded/partially-resolved event. Map explicitly, with 'idle' as
  // the non-assertive fallback — better to be vague than misleading.
  const mapBackendStatus = (raw: string | null | undefined): FeedStatus => {
    if (!raw) return 'idle'
    const v = String(raw).toLowerCase()
    if (v === 'live') return 'live'
    if (v === 'upcoming') return 'upcoming'
    if (v === 'closed' || v === 'inactive') return 'closed'
    // 'active', 'unknown', and any unrecognized string → idle.
    return 'idle'
  }

  // Semantic feed-status drives LivePulse's dot color, animation, and
  // accessible label together — so a screen-reader user hears "Live",
  // "Feed paused", or "Feed offline" consistent with the visual state.
  // Codex pass-4 HIGH 2026-04-19 fix.
  const feedStatus = useMemo<FeedStatus>(() => {
    if (currentEventId == null) return 'idle'
    if (statsError && stats) return 'paused' // poller alive, last poll failed transiently
    if (statsError) return 'offline' // initial fetch failed with nothing to fall back to
    if (!stats) return 'connecting'
    return mapBackendStatus(stats.event.status)
  }, [currentEventId, stats, statsError])

  const pulseCaptionOverride = useMemo<string | undefined>(() => {
    if (feedStatus === 'paused') return 'Feed paused · auto-retrying every 30 s'
    if (feedStatus === 'upcoming' && stats) return `Upcoming · ${stats.event.event_date}`
    // live, connecting, offline, closed, idle → let LivePulse compose its
    // default caption from the semantic status.
    return undefined
  }, [feedStatus, stats])

  if (currentEventId == null) {
    return (
      <PageLayout
        title="Analytics"
        subtitle="Pick an event from the switcher to see live metrics, capacity pacing, and kiosk health."
      >
        <div
          data-testid="analytics-empty"
          className="rounded-card border border-dashed border-hairline bg-surface-stripe p-8 text-center text-sm text-ink-soft shadow-card-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          No event in focus yet. Analytics will light up once you pick one.
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Analytics"
      subtitle={
        stats
          ? `${stats.event.event_name} · ${stats.event.event_date}`
          : 'Live event metrics'
      }
      actions={
        <LivePulse
          status={feedStatus}
          lastScanAt={stats?.generated_at ?? null}
          caption={pulseCaptionOverride}
        />
      }
    >
      {/* Row 1 — hero KPI tiles. 3 on lg+, 2 on md, 1 on sm. */}
      <section aria-label="Hero KPIs" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <HeroKpiProgressBar
          metric="revenue"
          value={stats?.metrics.revenue_total ?? null}
          capacity={deriveCapacity('revenue', stats)}
          yoyPct={yoy?.revenue_total ?? null}
          formatValue={fmtUSD}
          loading={statsLoading && !stats}
        />
        <HeroKpiProgressBar
          metric="riders_per_hr"
          value={stats?.metrics.riders_per_hour ?? null}
          capacity={deriveCapacity('riders_per_hr', stats)}
          yoyPct={yoy?.riders_per_hour ?? null}
          formatValue={fmtNum}
          loading={statsLoading && !stats}
        />
        <HeroKpiProgressBar
          metric="guests"
          value={stats?.metrics.guests ?? null}
          capacity={deriveCapacity('guests', stats)}
          yoyPct={yoy?.guests ?? null}
          formatValue={fmtNum}
          loading={statsLoading && !stats}
        />
      </section>

      {/* Row 2 — compact score tiles. Width-matches the hero row spacing. */}
      <section
        aria-label="Operational scores"
        className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      >
        <CompactScoreTile
          label="Kiosk Health"
          scorePct={extrasAvailable ? kioskHealth : null}
          caption={captionForScore(extrasAvailable ? kioskHealth : null)}
          loading={extrasLoading && !extras}
          unavailableMessage={
            extrasAvailable ? 'Need 2+ kiosks for health score' : 'Kiosk data unavailable'
          }
        />
        <CompactScoreTile
          label="Plan Mix Diversity"
          scorePct={extrasAvailable ? planDiversity : null}
          caption={captionForScore(extrasAvailable ? planDiversity : null)}
          loading={extrasLoading && !extras}
          unavailableMessage={
            extrasAvailable ? 'Need 2+ plan types to score' : 'Plan data unavailable'
          }
        />
      </section>

      {/* Row 3 — Revenue Heat Map + Capacity Gauges side by side */}
      <section
        aria-label="Visualizations"
        className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        <RevenueHeatMap
          hourly={stats?.hourly ?? []}
          loading={statsLoading && !stats}
        />
        <CapacityGauges
          hourly={stats?.hourly}
          kiosks={extras?.kiosk_throughput}
          metrics={stats?.metrics}
          loading={(statsLoading && !stats) || (extrasLoading && !extras)}
        />
      </section>

      {statsError && !stats ? (
        <p
          data-testid="analytics-stats-error"
          className="mt-4 text-xs text-ink-soft dark:text-slate-400"
        >
          Couldn't reach the live feed just yet. Auto-retrying every 30 seconds.
        </p>
      ) : null}
    </PageLayout>
  )
}
